import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ref, set } from 'firebase/database';
import { realtimeDb } from '../firebase/config';
import { FaShare, FaEnvelope, FaWhatsapp, FaFacebook, FaTwitter, FaLink, FaCopy } from 'react-icons/fa';
import './WishlistSharing.css';

const WishlistSharing = ({ wishlist = [] }) => {
  const { currentUser } = useAuth();
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const generateShareLink = async () => {
    if (!currentUser || wishlist.length === 0) return;

    try {
      const shareId = `${currentUser.uid}_${Date.now()}`;
      const shareData = {
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email,
        wishlist: wishlist,
        createdAt: Date.now(),
        expiresAt: Date.now() + (7 * 24 * 60 * 60 * 1000) // 7 days
      };

      // Save to Firebase
      await set(ref(realtimeDb, `sharedWishlists/${shareId}`), shareData);
      
      const link = `${window.location.origin}/shared-wishlist/${shareId}`;
      setShareLink(link);
      return link;
    } catch (error) {
      console.error('Error generating share link:', error);
      alert('Failed to generate share link. Please try again.');
    }
  };

  const handleShareClick = async () => {
    if (!currentUser) {
      alert('Please login to share your wishlist');
      return;
    }

    if (wishlist.length === 0) {
      alert('Your wishlist is empty. Add some products first!');
      return;
    }

    setShowShareModal(true);
    const link = await generateShareLink();
    setShareLink(link);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareViaEmail = async () => {
    if (!email.trim()) {
      alert('Please enter an email address');
      return;
    }

    if (!validateEmail(email)) {
      alert('Please enter a valid email address');
      return;
    }

    setSending(true);
    try {
      const subject = encodeURIComponent(`${currentUser.displayName || 'Someone'} shared their wishlist with you`);
      const body = encodeURIComponent(
        `${message}\n\nView their wishlist: ${shareLink}\n\nThis link will expire in 7 days.`
      );
      
      window.open(`mailto:${email}?subject=${subject}&body=${body}`);
    } catch (error) {
      console.error('Error sharing via email:', error);
      alert('Failed to open email client. Please copy the link manually.');
    } finally {
      setSending(false);
    }
  };

  const shareViaWhatsApp = () => {
    const text = encodeURIComponent(
      `${currentUser.displayName || 'Someone'} shared their wishlist with you!\n\nView it here: ${shareLink}`
    );
    window.open(`https://wa.me/?text=${text}`);
  };

  const shareViaFacebook = () => {
    const url = encodeURIComponent(shareLink);
    const text = encodeURIComponent(
      `${currentUser.displayName || 'Someone'} shared their wishlist with you!`
    );
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`);
  };

  const shareViaTwitter = () => {
    const text = encodeURIComponent(
      `${currentUser.displayName || 'Someone'} shared their wishlist with you! Check it out: ${shareLink}`
    );
    window.open(`https://twitter.com/intent/tweet?text=${text}`);
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const getWishlistSummary = () => {
    const totalItems = wishlist.length;
    const totalValue = wishlist.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);
    return { totalItems, totalValue };
  };

  const { totalItems, totalValue } = getWishlistSummary();

  return (
    <>
      <button 
        className="share-wishlist-btn"
        onClick={handleShareClick}
        disabled={wishlist.length === 0}
      >
        <FaShare />
        Share Wishlist ({totalItems} items)
      </button>

      {showShareModal && (
        <div className="share-modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="share-modal" onClick={(e) => e.stopPropagation()}>
            <div className="share-modal-header">
              <h3>Share Your Wishlist</h3>
              <button 
                className="close-btn"
                onClick={() => setShowShareModal(false)}
              >
                ×
              </button>
            </div>

            <div className="share-summary">
              <p>Share your wishlist with {totalItems} items (Total: ₹{totalValue})</p>
            </div>

            <div className="share-options">
              <div className="share-option">
                <h4>Share Link</h4>
                <div className="link-container">
                  <input 
                    type="text" 
                    value={shareLink} 
                    readOnly 
                    className="share-link-input"
                  />
                  <button 
                    className="copy-btn"
                    onClick={copyToClipboard}
                  >
                    <FaCopy />
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              <div className="share-option">
                <h4>Share via Email</h4>
                <div className="email-share">
                  <input
                    type="email"
                    placeholder="Enter email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="email-input"
                  />
                  <textarea
                    placeholder="Add a personal message (optional)"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="message-input"
                    rows={3}
                  />
                  <button 
                    className="email-share-btn"
                    onClick={shareViaEmail}
                    disabled={sending}
                  >
                    <FaEnvelope />
                    {sending ? 'Sending...' : 'Send Email'}
                  </button>
                </div>
              </div>

              <div className="share-option">
                <h4>Share on Social Media</h4>
                <div className="social-share-buttons">
                  <button 
                    className="social-btn whatsapp"
                    onClick={shareViaWhatsApp}
                  >
                    <FaWhatsapp />
                    WhatsApp
                  </button>
                  <button 
                    className="social-btn facebook"
                    onClick={shareViaFacebook}
                  >
                    <FaFacebook />
                    Facebook
                  </button>
                  <button 
                    className="social-btn twitter"
                    onClick={shareViaTwitter}
                  >
                    <FaTwitter />
                    Twitter
                  </button>
                </div>
              </div>
            </div>

            <div className="share-footer">
              <p className="share-note">
                <FaLink />
                This link will expire in 7 days for security
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WishlistSharing; 